/**
 * Stamp evaluation clips with the sequence / table / camera setup at record start
 * so uploads still match after the live session advances to the next sequence.
 */

import {
  DEFAULT_CAMERA_SETUP,
  filenameTokenForSetup,
  normalizeCameraSetup,
  orientedPlayerFromSetup,
  resolveCameraSetup,
} from "./evaluationCameraSetup";
import { tablePhoneShareFields } from "./tablePhoneCoverage";

export function normalizeCameraPosition(raw) {
  const p = String(raw || "")
    .trim()
    .toUpperCase();
  // C/D are player slots; map to camera ends A/B.
  if (p === "C") return "A";
  if (p === "D") return "B";
  return ["A", "B"].includes(p) ? p : null;
}

const SLOT_SIDES = ["A", "B", "C", "D"];

/** Identities of whoever is seated at a pinned table phone right now. */
export function tablePhoneAssignedPlayers(players) {
  const assignedPlayers = [];
  const shared = [];
  (Array.isArray(players) ? players : []).forEach((p, i) => {
    const playerId = p?.playerId ?? p?.player_id;
    const userId = p?.userId ?? p?.user_id;
    const pid = playerId != null ? String(playerId) : "";
    const uid = userId != null ? String(userId) : "";
    if (!pid && !uid) return;
    if (pid) shared.push(pid);
    if (uid && uid !== pid) shared.push(uid);
    if (pid) {
      assignedPlayers.push({
        position: p?.side || SLOT_SIDES[i] || "A",
        player_id: pid,
        role: p?.role || null,
      });
    }
  });
  return {
    assignedPlayers,
    sharedUserIds: Array.from(new Set(shared)),
    primaryPlayerId: assignedPlayers[0]?.player_id || null,
  };
}

export function assignedPlayersFromSequence(sequence) {
  if (!sequence || typeof sequence !== "object") return [];
  const rows = [
    {
      position: "A",
      player_id: sequence.player_a_id ?? sequence.playerAId ?? null,
      role: sequence.player_a_role ?? sequence.playerARole ?? null,
    },
    {
      position: "B",
      player_id: sequence.player_b_id ?? sequence.playerBId ?? null,
      role: sequence.player_b_role ?? sequence.playerBRole ?? null,
    },
  ];
  return rows.filter(
    (row) => row.player_id != null && String(row.player_id).trim()
  );
}

export function inferCameraPositionFromSequence(sequence, viewerPlayerId) {
  if (!sequence || viewerPlayerId == null) return null;
  const pid = String(viewerPlayerId);
  const a = sequence.player_a_id ?? sequence.playerAId;
  const b = sequence.player_b_id ?? sequence.playerBId;
  if (a != null && String(a) === pid) return "A";
  if (b != null && String(b) === pid) return "B";
  return null;
}

export function buildEvalClipUploadMeta({
  teId,
  sequenceNumber,
  sequence = null,
  viewerPlayerId = null,
  tableNumber = null,
  cameraPosition = null,
  cameraSetup = null,
  cameraOrdinal = null,
  recordedAt = null,
  flipH = false,
  flipV = false,
  kotMatchId = null,
  tablePhone = false,
  tablePlayers = null,
  tableNumbers = null,
} = {}) {
  const seq = Number(sequenceNumber);
  const sequenceOrder = Number.isFinite(seq) && seq > 0 ? seq : null;
  const table =
    tableNumber != null && Number(tableNumber) > 0 ? Number(tableNumber) : null;
  const resolvedSetup =
    resolveCameraSetup({
      bodySetup: cameraSetup,
      cameraPosition:
        normalizeCameraPosition(cameraPosition) ||
        inferCameraPositionFromSequence(sequence, viewerPlayerId),
    }) || DEFAULT_CAMERA_SETUP;
  const camera = orientedPlayerFromSetup(resolvedSetup);
  const seated = tablePhone ? tablePhoneAssignedPlayers(tablePlayers) : null;
  const assignedPlayers = seated?.assignedPlayers?.length
    ? seated.assignedPlayers
    : assignedPlayersFromSequence(sequence);
  const recordedIso = recordedAt
    ? new Date(recordedAt).toISOString()
    : new Date().toISOString();
  const ordinal =
    cameraOrdinal != null && Number(cameraOrdinal) >= 1
      ? Math.floor(Number(cameraOrdinal))
      : null;
  const viewSetup = {
    sequence_number: sequenceOrder,
    table_number: table,
    camera_setup: resolvedSetup,
    camera_position: camera,
    positions: camera ? [camera] : [],
    assigned_players: assignedPlayers,
    recorded_at: recordedIso,
    flip_h: flipH === true,
    flip_v: flipV === true,
  };
  if (ordinal != null) viewSetup.camera_ordinal = ordinal;
  if (tablePhone) {
    const share = tablePhoneShareFields(table, tableNumbers);
    viewSetup.table_phone = true;
    if (share) {
      viewSetup.table_numbers = share.table_numbers;
      if (viewSetup.table_number == null) {
        viewSetup.table_number = share.table_number;
      }
    }
    if (seated?.sharedUserIds?.length) {
      viewSetup.shared_user_ids = seated.sharedUserIds;
    }
  }
  if (kotMatchId) {
    viewSetup.kot_match_id = String(kotMatchId);
  } else if (!(Number.isFinite(sequenceOrder) && sequenceOrder > 0)) {
    // Full personal / match exercise clip (no sequence window).
    viewSetup.personal_match = true;
    viewSetup.clip_kind = "personal_match";
  }
  return {
    teId: teId != null ? String(teId) : null,
    sequenceNumber: sequenceOrder,
    playerId:
      (tablePhone && seated?.primaryPlayerId) ||
      (viewerPlayerId != null ? String(viewerPlayerId) : null),
    tableNumber: table,
    cameraPosition: camera,
    cameraSetup: resolvedSetup,
    cameraOrdinal: ordinal,
    assignedPlayers,
    kotMatchId: kotMatchId != null ? String(kotMatchId) : null,
    viewSetup,
  };
}

export function evalClipFilename({
  tableNumber = null,
  cameraPosition = null,
  cameraSetup = null,
  sequenceNumber = null,
  teId = null,
  ext = "mp4",
  kotMatchId = null,
} = {}) {
  const safeExt = String(ext || "mp4").replace(/^\./, "") || "mp4";
  const teStem =
    String(teId || "te")
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 8) || "te";
  if (kotMatchId) {
    return `SmartTT_web_kot_${String(kotMatchId).slice(0, 8)}.${safeExt}`;
  }
  const seq =
    sequenceNumber != null && Number(sequenceNumber) > 0
      ? Number(sequenceNumber)
      : null;
  const table =
    tableNumber != null && Number(tableNumber) > 0 ? Number(tableNumber) : 0;
  const setup =
    normalizeCameraSetup(cameraSetup) ||
    resolveCameraSetup({ cameraPosition }) ||
    DEFAULT_CAMERA_SETUP;
  const token = filenameTokenForSetup(setup);
  if (seq) {
    return `SmartTT_T${table}_${token}_seq${seq}_${teStem}.${safeExt}`;
  }
  return `SmartTT_web_${teStem}.${safeExt}`;
}
